import { FC, useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from 'src/trpc';
import { coverageAreaZ } from '@libs/schemas';
import { z } from 'zod';
import { Loader } from 'src/components/Loader';

// Use the existing schema, omitting fields that aren't part of the form
const coverageAreaFormSchema = coverageAreaZ.omit({
  _id: true,
  createdAt: true,
  updatedAt: true,
});

type CoverageAreaFormData = z.infer<typeof coverageAreaFormSchema>;

interface CoverageAreaDetailsFormProps {
  coverageAreaId?: string;
  organizationId?: string;
  onSuccess: () => void;
  onError: (error: Error) => void;
  onStateChange: (state: { isValid: boolean; isDirty: boolean }) => void;
  children?: React.ReactNode;
}

export const CoverageAreaDetailsForm: FC<CoverageAreaDetailsFormProps> = ({
  coverageAreaId,
  organizationId,
  onSuccess,
  onError,
  onStateChange,
  children,
}) => {
  const isEditing = Boolean(coverageAreaId);

  // Fetch existing coverage area data for editing
  const { data: existingCoverageArea, isLoading } =
    trpc.admin.organization.getCoverageArea.useQuery(coverageAreaId!, {
      enabled: isEditing,
    });

  const form = useForm<CoverageAreaFormData>({
    resolver: zodResolver(coverageAreaFormSchema),
    defaultValues: {
      zipCode: '',
      city: '',
      state: '',
      active: true,
      organizationId: organizationId || undefined,
    },
  });

  const { isValid, isDirty } = useFormState({ control: form.control });

  // Update form values when existing coverage area data is loaded
  useEffect(() => {
    if (existingCoverageArea) {
      form.reset(existingCoverageArea);
    }
  }, [existingCoverageArea, form]);

  useEffect(() => {
    onStateChange({ isValid, isDirty });
  }, [isValid, isDirty, onStateChange]);

  const createMutation = trpc.admin.organization.createCoverageArea.useMutation({
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      onError(new Error(error.message || 'Failed to create coverage area'));
    },
  });

  const updateMutation = trpc.admin.organization.updateCoverageArea.useMutation({
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      onError(new Error(error.message || 'Failed to update coverage area'));
    },
  });

  const onSubmit = (data: CoverageAreaFormData) => {
    if (isEditing) {
      updateMutation.mutate({
        _id: coverageAreaId!,
        ...data,
      });
    } else {
      createMutation.mutate(data);
    }
  };

  if (isEditing && isLoading) {
    return (
      <div className="py-8">
        <Loader title="Loading coverage area..." />
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="zipCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label">Zip Code</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter zip code"
                    className="text-text-light"
                    autoFocus={false}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label">City</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter city"
                    className="text-text-light"
                    autoFocus={false}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label">State</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter state"
                    className="text-text-light"
                    autoFocus={false}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="active"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label">Status</FormLabel>
                <div className="flex items-center space-x-2">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className="text-sm">Active (accepting orders)</FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {children}
      </form>
    </Form>
  );
};
